'use client';

import React, { useState } from 'react';
import { FileSearch, Plus, Download, ChevronDown, ChevronUp, CheckCircle2, XCircle, Star, DollarSign, Cpu, Briefcase, ShieldAlert, Scale, BarChart2, Search, Eye, Edit2, TrendingUp, Award, Users, ClipboardList, ExternalLink, Home, ChevronRight as ChevronRightIcon, AlertTriangle, Clock, CheckSquare, Database, Server } from 'lucide-react';
import Link from 'next/link';
import { toast } from 'sonner';
import { useRole } from '@/contexts/RoleContext';

// ─── Types ────────────────────────────────────────────────────────────────────

type RFPStatus = 'Draft' | 'Published' | 'Evaluation' | 'Awarded' | 'Cancelled' | 'Closed';

interface RFP {
  id: string;
  title: string;
  department: string;
  status: RFPStatus;
  publishDate: string;
  closeDate: string;
  estimatedValue: number;
  bidderCount: number;
  category: string;
  owner: string;
  intakeRef?: string;
  awardedVendorId?: string;
  awardedVendorName?: string;
}

interface EvalScore {
  cost: number;
  technicalFit: number;
  experience: number;
  securityPosture: number;
  responsibleBidder: number;
}

interface Bidder {
  id: string;
  rfpId: string;
  company: string;
  contactName: string;
  submittedDate: string;
  quoteAmount: number;
  status: 'Received' | 'Under Review' | 'Shortlisted' | 'Disqualified' | 'Awarded';
  scores: EvalScore;
  notes: string;
  certifications: string[];
  yearsExperience: number;
  localVendor: boolean;
  minorityOwned: boolean;
  vendorId?: string;
}

interface IntakeItem {
  id: string;
  intakeRef: string;
  projectTitle: string;
  department: string;
  requestedBy: string;
  submittedDate: string;
  urgency: 'routine' | 'standard' | 'expedited' | 'emergency';
  estimatedValue: string;
  riskTier: 'Critical' | 'High' | 'Moderate' | 'Low';
  competitiveBid: string;
  status: 'Pending RFP' | 'RFP Created' | 'Sole Source Approved' | 'Needs Review';
  dataTypes: string[];
  systemsAccessed: string[];
  notes: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const INTAKE_QUEUE: IntakeItem[] = [
  {
    id: 'int-001',
    intakeRef: 'INT-2026-4821',
    projectTitle: 'Enterprise IT Security & SIEM Platform',
    department: 'Information Technology',
    requestedBy: 'Kevin Tanaka',
    submittedDate: '2025-11-10',
    urgency: 'standard',
    estimatedValue: '$480,000',
    riskTier: 'Critical',
    competitiveBid: 'Yes',
    status: 'RFP Created',
    dataTypes: ['Employee Data', 'System Logs', 'Network Data'],
    systemsAccessed: ['City Network', 'AD/Identity', 'SIEM'],
    notes: 'Linked to RFP-001. Evaluation in progress.',
  },
  {
    id: 'int-002',
    intakeRef: 'INT-2026-3107',
    projectTitle: 'Citywide Fiber Network Infrastructure Upgrade',
    department: 'Public Works',
    requestedBy: 'Tomás Rivera',
    submittedDate: '2026-01-15',
    urgency: 'standard',
    estimatedValue: '$2,100,000',
    riskTier: 'High',
    competitiveBid: 'Yes',
    status: 'RFP Created',
    dataTypes: ['Infrastructure Data', 'Network Data'],
    systemsAccessed: ['GIS', 'Network Infrastructure'],
    notes: 'Linked to RFP-002. Bids received.',
  },
  {
    id: 'int-003',
    intakeRef: 'INT-2026-5592',
    projectTitle: 'Fleet Telematics & GPS Tracking System',
    department: 'Public Works',
    requestedBy: 'Sandra Kim',
    submittedDate: '2026-02-20',
    urgency: 'routine',
    estimatedValue: '$95,000',
    riskTier: 'Moderate',
    competitiveBid: 'Yes',
    status: 'Pending RFP',
    dataTypes: ['Vehicle Location Data', 'Operational Records'],
    systemsAccessed: ['Fleet Management System'],
    notes: 'Awaiting RFP creation. Draft RFP-004 in progress.',
  },
  {
    id: 'int-004',
    intakeRef: 'INT-2026-6034',
    projectTitle: 'Cybersecurity Awareness Training Platform',
    department: 'Information Technology',
    requestedBy: 'Priya Nair',
    submittedDate: '2026-03-01',
    urgency: 'expedited',
    estimatedValue: '$45,000',
    riskTier: 'Low',
    competitiveBid: 'Yes',
    status: 'Pending RFP',
    dataTypes: ['Employee Records'],
    systemsAccessed: ['Microsoft 365', 'Active Directory / Azure AD'],
    notes: 'Expedited due to upcoming compliance audit. Needs RFP by end of month.',
  },
  {
    id: 'int-005',
    intakeRef: 'INT-2026-6218',
    projectTitle: 'Legal Document Management System',
    department: 'City Attorney',
    requestedBy: 'Robert Castillo',
    submittedDate: '2026-03-10',
    urgency: 'standard',
    estimatedValue: '$120,000',
    riskTier: 'High',
    competitiveBid: 'No',
    status: 'Needs Review',
    dataTypes: ['Confidential City Records', 'Legal Documents', 'Personally Identifiable Information (PII)'],
    systemsAccessed: ['City Attorney Document System', 'Microsoft 365'],
    notes: 'Sole source justification submitted. Pending Procurement Officer review.',
  },
];

const RFPS: RFP[] = [
  {
    id: 'rfp-001',
    title: 'Enterprise IT Security & SIEM Platform',
    department: 'Information Technology',
    status: 'Evaluation',
    publishDate: '2025-11-15',
    closeDate: '2026-01-10',
    estimatedValue: 480000,
    bidderCount: 5,
    category: 'Technology',
    owner: 'Marcus Webb',
    intakeRef: 'INT-2026-4821',
  },
  {
    id: 'rfp-002',
    title: 'Citywide Fiber Network Infrastructure Upgrade',
    department: 'Public Works',
    status: 'Published',
    publishDate: '2026-01-20',
    closeDate: '2026-03-15',
    estimatedValue: 2100000,
    bidderCount: 3,
    category: 'Infrastructure',
    owner: 'Sandra Kim',
    intakeRef: 'INT-2026-3107',
  },
  {
    id: 'rfp-003',
    title: 'HR & Payroll Management System',
    department: 'Human Resources',
    status: 'Awarded',
    publishDate: '2025-09-01',
    closeDate: '2025-11-01',
    estimatedValue: 320000,
    bidderCount: 6,
    category: 'Software',
    owner: 'James Okafor',
    awardedVendorId: 'vendor-005',
    awardedVendorName: 'ADP TotalSource',
  },
  {
    id: 'rfp-004',
    title: 'Fleet Telematics & GPS Tracking',
    department: 'Public Works',
    status: 'Draft',
    publishDate: '—',
    closeDate: '2026-04-30',
    estimatedValue: 95000,
    bidderCount: 0,
    category: 'Technology',
    owner: 'Marcus Webb',
    intakeRef: 'INT-2026-5592',
  },
  {
    id: 'rfp-005',
    title: 'Community Recreation Software Platform',
    department: 'Parks & Recreation',
    status: 'Closed',
    publishDate: '2025-07-01',
    closeDate: '2025-09-01',
    estimatedValue: 140000,
    bidderCount: 4,
    category: 'Software',
    owner: 'Lisa Tran',
  },
];

const BIDDERS: Bidder[] = [
  {
    id: 'b-001',
    rfpId: 'rfp-001',
    company: 'CyberShield Solutions Inc.',
    contactName: 'Priya Nair',
    submittedDate: '2026-01-08',
    quoteAmount: 412000,
    status: 'Shortlisted',
    scores: { cost: 88, technicalFit: 92, experience: 85, securityPosture: 95, responsibleBidder: 78 },
    notes: 'Strong SIEM expertise; FedRAMP certified. Pricing competitive.',
    certifications: ['FedRAMP', 'SOC 2 Type II', 'ISO 27001'],
    yearsExperience: 12,
    localVendor: false,
    minorityOwned: false,
  },
  {
    id: 'b-002',
    rfpId: 'rfp-001',
    company: 'Nexus Cyber Group',
    contactName: 'Derek Hollis',
    submittedDate: '2026-01-09',
    quoteAmount: 389000,
    status: 'Shortlisted',
    scores: { cost: 92, technicalFit: 80, experience: 72, securityPosture: 88, responsibleBidder: 85 },
    notes: 'Lower cost but less municipal experience. Good references.',
    certifications: ['SOC 2 Type II', 'CISA Partner'],
    yearsExperience: 7,
    localVendor: true,
    minorityOwned: true,
  },
  {
    id: 'b-003',
    rfpId: 'rfp-001',
    company: 'Fortis IT Security LLC',
    contactName: 'Angela Reyes',
    submittedDate: '2026-01-07',
    quoteAmount: 465000,
    status: 'Under Review',
    scores: { cost: 72, technicalFit: 88, experience: 94, securityPosture: 91, responsibleBidder: 90 },
    notes: 'Extensive gov sector experience. Premium pricing.',
    certifications: ['FedRAMP', 'FISMA', 'SOC 2 Type II', 'ISO 27001'],
    yearsExperience: 18,
    localVendor: false,
    minorityOwned: false,
  },
  {
    id: 'b-004',
    rfpId: 'rfp-001',
    company: 'DataGuard Municipal Services',
    contactName: 'Tom Brennan',
    submittedDate: '2026-01-10',
    quoteAmount: 502000,
    status: 'Under Review',
    scores: { cost: 65, technicalFit: 76, experience: 80, securityPosture: 82, responsibleBidder: 88 },
    notes: 'Specialized in municipal deployments. Above budget.',
    certifications: ['SOC 2 Type I', 'StateRAMP'],
    yearsExperience: 9,
    localVendor: false,
    minorityOwned: true,
  },
  {
    id: 'b-005',
    rfpId: 'rfp-001',
    company: 'Vanguard Tech Partners',
    contactName: 'Michelle Park',
    submittedDate: '2026-01-06',
    quoteAmount: 358000,
    status: 'Disqualified',
    scores: { cost: 95, technicalFit: 55, experience: 48, securityPosture: 60, responsibleBidder: 52 },
    notes: 'Disqualified — failed mandatory security requirements.',
    certifications: [],
    yearsExperience: 3,
    localVendor: true,
    minorityOwned: false,
  },
  {
    id: 'b-006',
    rfpId: 'rfp-002',
    company: 'Pacific Fiber Networks',
    contactName: 'Carlos Mendez',
    submittedDate: '2026-03-10',
    quoteAmount: 1980000,
    status: 'Received',
    scores: { cost: 85, technicalFit: 90, experience: 88, securityPosture: 75, responsibleBidder: 82 },
    notes: 'Strong regional presence. Competitive pricing.',
    certifications: ['ISO 9001', 'BICSI'],
    yearsExperience: 15,
    localVendor: true,
    minorityOwned: false,
  },
  {
    id: 'b-007',
    rfpId: 'rfp-003',
    company: 'ADP TotalSource',
    contactName: 'Jennifer Walsh',
    submittedDate: '2025-10-28',
    quoteAmount: 310000,
    status: 'Awarded',
    scores: { cost: 88, technicalFit: 94, experience: 96, securityPosture: 90, responsibleBidder: 85 },
    notes: 'Best-in-class HR/payroll platform. Awarded and onboarded.',
    certifications: ['SOC 2 Type II', 'ISO 27001', 'FedRAMP'],
    yearsExperience: 25,
    localVendor: false,
    minorityOwned: false,
    vendorId: 'vendor-005',
  },
];

// ─── Weights ──────────────────────────────────────────────────────────────────

const EVAL_WEIGHTS: Record<keyof EvalScore, number> = {
  cost: 0.30,
  technicalFit: 0.25,
  experience: 0.20,
  securityPosture: 0.15,
  responsibleBidder: 0.10,
};

const EVAL_LABELS: Record<keyof EvalScore, string> = {
  cost: 'Cost',
  technicalFit: 'Technical Fit',
  experience: 'Experience',
  securityPosture: 'Security Posture',
  responsibleBidder: 'Responsible Bidder',
};

const EVAL_ICONS: Record<keyof EvalScore, React.ElementType> = {
  cost: DollarSign,
  technicalFit: Cpu,
  experience: Briefcase,
  securityPosture: ShieldAlert,
  responsibleBidder: Scale,
};

const EVAL_COLORS: Record<keyof EvalScore, string> = {
  cost: 'text-emerald-600',
  technicalFit: 'text-blue-600',
  experience: 'text-violet-600',
  securityPosture: 'text-orange-600',
  responsibleBidder: 'text-teal-600',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function weightedScore(scores: EvalScore): number {
  return Math.round(
    scores.cost * EVAL_WEIGHTS.cost +
    scores.technicalFit * EVAL_WEIGHTS.technicalFit +
    scores.experience * EVAL_WEIGHTS.experience +
    scores.securityPosture * EVAL_WEIGHTS.securityPosture +
    scores.responsibleBidder * EVAL_WEIGHTS.responsibleBidder
  );
}

function scoreColor(score: number): string {
  if (score >= 85) return 'text-[hsl(var(--positive))]';
  if (score >= 70) return 'text-[hsl(var(--warning))]';
  return 'text-[hsl(var(--negative))]';
}

function scoreBg(score: number): string {
  if (score >= 85) return 'bg-[hsl(var(--positive-bg))]';
  if (score >= 70) return 'bg-[hsl(var(--warning-bg))]';
  return 'bg-[hsl(var(--negative-bg))]';
}

function rfpStatusColor(status: RFPStatus): string {
  const map: Record<RFPStatus, string> = {
    Draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    Published: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400',
    Evaluation: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
    Awarded: 'bg-[hsl(var(--positive-bg))] text-[hsl(var(--positive))]',
    Cancelled: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
    Closed: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500',
  };
  return map[status];
}

function rfpStatusIcon(status: RFPStatus) {
  const map: Record<RFPStatus, React.ElementType> = {
    Draft: Edit2,
    Published: Eye,
    Evaluation: BarChart2,
    Awarded: Award,
    Cancelled: XCircle,
    Closed: CheckCircle2,
  };
  const Ic = map[status];
  return <Ic size={11} />;
}

function bidderStatusColor(status: Bidder['status']): string {
  const map: Record<Bidder['status'], string> = {
    Received: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400',
    'Under Review': 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
    Shortlisted: 'bg-[hsl(var(--positive-bg))] text-[hsl(var(--positive))]',
    Disqualified: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
    Awarded: 'bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-400',
  };
  return map[status];
}

function urgencyColor(urgency: IntakeItem['urgency']): string {
  const map: Record<IntakeItem['urgency'], string> = {
    routine: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    standard: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400',
    expedited: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
    emergency: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
  };
  return map[urgency];
}

function intakeStatusColor(status: IntakeItem['status']): string {
  const map: Record<IntakeItem['status'], string> = {
    'Pending RFP': 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
    'RFP Created': 'bg-[hsl(var(--positive-bg))] text-[hsl(var(--positive))]',
    'Sole Source Approved': 'bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-400',
    'Needs Review': 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
  };
  return map[status];
}

function riskTierColor(tier: IntakeItem['riskTier']): string {
  const map: Record<IntakeItem['riskTier'], string> = {
    Critical: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
    High: 'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-400',
    Moderate: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
    Low: 'bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-400',
  };
  return map[tier];
}

function fmt(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

// ─── Score Bar ────────────────────────────────────────────────────────────────

function ScoreBar({ score, max = 100 }: { score: number; max?: number }) {
  const pct = (score / max) * 100;
  const color = score >= 85 ? 'bg-[hsl(var(--positive))]' : score >= 70 ? 'bg-[hsl(var(--warning))]' : 'bg-[hsl(var(--negative))]';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-[hsl(var(--border))] rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-semibold tabular-nums w-7 text-right ${scoreColor(score)}`}>{score}</span>
    </div>
  );
}

// ─── RFP Status Card ──────────────────────────────────────────────────────────

function RFPStatusCard({ rfp, selected, onClick }: { rfp: RFP; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-lg border transition-all duration-150 ${
        selected
          ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent-light))]'
          : 'border-[hsl(var(--border))] bg-[hsl(var(--surface))] hover:border-[hsl(var(--border-strong))]'
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-xs font-semibold text-[hsl(var(--text-primary))] leading-snug line-clamp-2">{rfp.title}</p>
        <span className={`inline-flex items-center gap-1 text-2xs font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${rfpStatusColor(rfp.status)}`}>
          {rfpStatusIcon(rfp.status)}
          {rfp.status}
        </span>
      </div>
      <p className="text-2xs text-[hsl(var(--text-muted))] mb-2">{rfp.department}</p>
      <div className="flex items-center justify-between">
        <span className="text-2xs text-[hsl(var(--text-secondary))]">{fmt(rfp.estimatedValue)}</span>
        <span className="text-2xs text-[hsl(var(--text-muted))]">
          {rfp.bidderCount} bidder{rfp.bidderCount !== 1 ? 's' : ''}
        </span>
      </div>
      {rfp.intakeRef && (
        <div className="mt-1.5 flex items-center gap-1 text-2xs text-[hsl(var(--text-muted))]">
          <ClipboardList size={10} />
          <span>{rfp.intakeRef}</span>
        </div>
      )}
    </button>
  );
}

// ─── Comparison Matrix ────────────────────────────────────────────────────────

function ComparisonMatrix({ bidders }: { bidders: Bidder[] }) {
  const [sortKey, setSortKey] = useState<'weighted' | keyof EvalScore | 'cost'>('weighted');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const { can } = useRole();

  const evalKeys = Object.keys(EVAL_WEIGHTS) as (keyof EvalScore)[];

  const sorted = [...bidders].sort((a, b) => {
    let av = 0, bv = 0;
    if (sortKey === 'weighted') { av = weightedScore(a.scores); bv = weightedScore(b.scores); }
    else if (sortKey === 'cost') { av = a.quoteAmount; bv = b.quoteAmount; }
    else { av = a.scores[sortKey as keyof EvalScore]; bv = b.scores[sortKey as keyof EvalScore]; }
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortIcon = ({ col }: { col: typeof sortKey }) =>
    sortKey === col ? (
      sortDir === 'desc' ? <ChevronDown size={11} className="text-[hsl(var(--accent))]" /> : <ChevronUp size={11} className="text-[hsl(var(--accent))]" />
    ) : (
      <ChevronDown size={11} className="text-[hsl(var(--text-muted))] opacity-40" />
    );

  const activeBidders = bidders.filter((b) => b.status !== 'Disqualified');
  const bestScore = Math.max(...activeBidders.map((b) => weightedScore(b.scores)));
  const lowestCost = Math.min(...activeBidders.map((b) => b.quoteAmount));

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse min-w-[700px]">
        <thead>
          <tr className="border-b border-[hsl(var(--border))]">
            <th className="text-left py-2.5 px-3 text-[hsl(var(--text-muted))] font-medium w-44">Bidder</th>
            <th
              className="text-right py-2.5 px-3 text-[hsl(var(--text-muted))] font-medium cursor-pointer hover:text-[hsl(var(--text-primary))] select-none"
              onClick={() => handleSort('cost')}
            >
              <span className="inline-flex items-center gap-1 justify-end">Quote <SortIcon col="cost" /></span>
            </th>
            {evalKeys.map((k) => {
              const Ic = EVAL_ICONS[k];
              return (
                <th
                  key={k}
                  className="text-center py-2.5 px-2 text-[hsl(var(--text-muted))] font-medium cursor-pointer hover:text-[hsl(var(--text-primary))] select-none"
                  onClick={() => handleSort(k)}
                >
                  <span className="inline-flex flex-col items-center gap-0.5">
                    <span className="inline-flex items-center gap-1">
                      <Ic size={11} className={EVAL_COLORS[k]} />
                      <SortIcon col={k} />
                    </span>
                    <span className="text-2xs">{EVAL_LABELS[k]}</span>
                    <span className="text-2xs text-[hsl(var(--text-muted))] opacity-60">{Math.round(EVAL_WEIGHTS[k] * 100)}%</span>
                  </span>
                </th>
              );
            })}
            <th
              className="text-center py-2.5 px-3 text-[hsl(var(--text-muted))] font-medium cursor-pointer hover:text-[hsl(var(--text-primary))] select-none"
              onClick={() => handleSort('weighted')}
            >
              <span className="inline-flex items-center gap-1 justify-center">
                Weighted <SortIcon col="weighted" />
              </span>
            </th>
            <th className="text-left py-2.5 px-3 text-[hsl(var(--text-muted))] font-medium">Status</th>
            <th className="text-left py-2.5 px-3 text-[hsl(var(--text-muted))] font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((bidder) => {
            const ws = weightedScore(bidder.scores);
            const isDisq = bidder.status === 'Disqualified';
            const isBest = !isDisq && ws === bestScore;
            const isCheapest = !isDisq && bidder.quoteAmount === lowestCost;
            const isAwarded = bidder.status === 'Awarded';
            return (
              <tr
                key={bidder.id}
                className={`border-b border-[hsl(var(--border))] transition-colors ${
                  isDisq ? 'opacity-50' : 'hover:bg-[hsl(var(--background))]'
                } ${isBest ? 'bg-[hsl(var(--accent-light))]' : ''}`}
              >
                <td className="py-3 px-3">
                  <div className="flex items-start gap-1.5">
                    {isBest && <Star size={11} className="text-amber-500 flex-shrink-0 mt-0.5" />}
                    <div>
                      <p className="font-semibold text-[hsl(var(--text-primary))] leading-snug">{bidder.company}</p>
                      <p className="text-2xs text-[hsl(var(--text-muted))]">{bidder.contactName}</p>
                      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                        {bidder.localVendor && (
                          <span className="text-2xs bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-400 px-1 rounded">Local</span>
                        )}
                        {bidder.minorityOwned && (
                          <span className="text-2xs bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-400 px-1 rounded">MBE</span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className={`font-semibold tabular-nums ${isCheapest ? 'text-[hsl(var(--positive))]' : 'text-[hsl(var(--text-primary))]'}`}>
                    {fmt(bidder.quoteAmount)}
                  </span>
                  {isCheapest && <p className="text-2xs text-[hsl(var(--positive))]">Lowest</p>}
                </td>
                {evalKeys.map((k) => (
                  <td key={k} className="py-3 px-2">
                    <ScoreBar score={bidder.scores[k]} />
                  </td>
                ))}
                <td className="py-3 px-3 text-center">
                  <span className={`inline-block text-sm font-bold tabular-nums px-2 py-0.5 rounded-md ${scoreBg(ws)} ${scoreColor(ws)}`}>
                    {ws}
                  </span>
                </td>
                <td className="py-3 px-3">
                  <span className={`inline-flex items-center text-2xs font-semibold px-2 py-0.5 rounded-full ${bidderStatusColor(bidder.status)}`}>
                    {bidder.status}
                  </span>
                </td>
                <td className="py-3 px-3">
                  <div className="flex items-center gap-1.5">
                    {isAwarded && bidder.vendorId ? (
                      <Link
                        href={`/vendor-workspace/${bidder.vendorId}`}
                        className="inline-flex items-center gap-1 text-2xs font-semibold px-2 py-1 rounded bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-400 hover:bg-teal-200 dark:hover:bg-teal-900 transition-colors"
                      >
                        <ExternalLink size={10} /> Vendor Record
                      </Link>
                    ) : !isDisq && !isAwarded && can('sourcing:awardRFP') ? (
                      <button
                        onClick={() => toast.success(`Award initiated for ${bidder.company}`, { description: 'Vendor will be promoted to onboarding.' })}
                        className="inline-flex items-center gap-1 text-2xs font-semibold px-2 py-1 rounded bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900 transition-colors"
                      >
                        <Award size={10} /> Award
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Evaluation Scoring Panel ─────────────────────────────────────────────────

function EvaluationScoringPanel({ bidders }: { bidders: Bidder[] }) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [localScores, setLocalScores] = useState<Record<string, EvalScore>>({});
  const { can } = useRole();

  const evalKeys = Object.keys(EVAL_WEIGHTS) as (keyof EvalScore)[];
  const activeBidders = bidders.filter((b) => b.status !== 'Disqualified');

  const getScore = (bidder: Bidder, key: keyof EvalScore): number =>
    localScores[bidder.id]?.[key] ?? bidder.scores[key];

  const handleScoreChange = (bidderId: string, key: keyof EvalScore, val: number) => {
    setLocalScores((prev) => ({
      ...prev,
      [bidderId]: { ...(prev[bidderId] ?? bidders.find((b) => b.id === bidderId)!.scores), [key]: val },
    }));
  };

  const handleSave = (bidderId: string) => {
    setEditingId(null);
    toast.success('Scores saved', { description: 'Evaluation scores updated successfully.' });
  };

  return (
    <div className="space-y-3">
      {activeBidders.map((bidder) => {
        const isEditing = editingId === bidder.id;
        const scores = evalKeys.reduce((acc, k) => ({ ...acc, [k]: getScore(bidder, k) }), {} as EvalScore);
        const ws = weightedScore(scores);

        return (
          <div key={bidder.id} className="border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--surface))] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--background))]">
              <div className="flex items-center gap-3">
                <div>
                  <p className="text-sm font-semibold text-[hsl(var(--text-primary))]">{bidder.company}</p>
                  <p className="text-2xs text-[hsl(var(--text-muted))]">{bidder.yearsExperience} yrs exp · {bidder.certifications.join(', ') || 'No certifications'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-2xs text-[hsl(var(--text-muted))]">Weighted Score</p>
                  <p className={`text-lg font-bold tabular-nums ${scoreColor(ws)}`}>{ws}<span className="text-xs font-normal">/100</span></p>
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${bidderStatusColor(bidder.status)}`}>{bidder.status}</span>
                {bidder.status === 'Awarded' && bidder.vendorId && (
                  <Link
                    href={`/vendor-workspace/${bidder.vendorId}`}
                    className="btn-ghost text-xs py-1 px-2.5 flex items-center gap-1"
                  >
                    <ExternalLink size={12} /> Record
                  </Link>
                )}
                {isEditing ? (
                  <div className="flex gap-1.5">
                    <button onClick={() => handleSave(bidder.id)} className="btn-primary text-xs py-1 px-2.5">Save</button>
                    <button onClick={() => setEditingId(null)} className="btn-secondary text-xs py-1 px-2.5">Cancel</button>
                  </div>
                ) : can('sourcing:editScores') ? (
                  <button onClick={() => setEditingId(bidder.id)} className="btn-ghost text-xs py-1 px-2.5">
                    <Edit2 size={11} /> Edit Scores
                  </button>
                ) : null}
              </div>
            </div>

            <div className="grid grid-cols-5 divide-x divide-[hsl(var(--border))]">
              {evalKeys.map((k) => {
                const Ic = EVAL_ICONS[k];
                const score = getScore(bidder, k);
                return (
                  <div key={k} className="px-4 py-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Ic size={13} className={EVAL_COLORS[k]} />
                      <span className="text-2xs font-medium text-[hsl(var(--text-secondary))]">{EVAL_LABELS[k]}</span>
                      <span className="text-2xs text-[hsl(var(--text-muted))] ml-auto">{Math.round(EVAL_WEIGHTS[k] * 100)}%</span>
                    </div>
                    {isEditing ? (
                      <div className="space-y-1">
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={score}
                          onChange={(e) => handleScoreChange(bidder.id, k, Number(e.target.value))}
                          className="w-full h-1.5 accent-[hsl(var(--accent))]"
                        />
                        <div className="flex justify-between">
                          <span className="text-2xs text-[hsl(var(--text-muted))]">0</span>
                          <span className={`text-sm font-bold tabular-nums ${scoreColor(score)}`}>{score}</span>
                          <span className="text-2xs text-[hsl(var(--text-muted))]">100</span>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className={`text-xl font-bold tabular-nums ${scoreColor(score)}`}>{score}</span>
                          <span className="text-2xs text-[hsl(var(--text-muted))]">/100</span>
                        </div>
                        <div className="h-1.5 bg-[hsl(var(--border))] rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${score >= 85 ? 'bg-[hsl(var(--positive))]' : score >= 70 ? 'bg-[hsl(var(--warning))]' : 'bg-[hsl(var(--negative))]'}`}
                            style={{ width: `${score}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="px-4 py-2.5 border-t border-[hsl(var(--border))] bg-[hsl(var(--background))] flex items-center gap-2">
              <span className="text-2xs text-[hsl(var(--text-muted))]">Notes:</span>
              <span className="text-2xs text-[hsl(var(--text-secondary))]">{bidder.notes}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Quote Collection Panel ───────────────────────────────────────────────────

function QuoteCollectionPanel({ bidders }: { bidders: Bidder[] }) {
  return (
    <div className="space-y-2">
      {bidders.map((bidder) => (
        <div
          key={bidder.id}
          className="flex items-center gap-4 px-4 py-3 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--surface))] hover:border-[hsl(var(--border-strong))] transition-colors"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <p className="text-sm font-semibold text-[hsl(var(--text-primary))] truncate">{bidder.company}</p>
              {bidder.localVendor && <span className="text-2xs bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-400 px-1.5 rounded-full">Local</span>}
              {bidder.minorityOwned && <span className="text-2xs bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-400 px-1.5 rounded-full">MBE</span>}
            </div>
            <p className="text-2xs text-[hsl(var(--text-muted))]">Contact: {bidder.contactName} · Submitted: {bidder.submittedDate}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-sm font-bold tabular-nums text-[hsl(var(--text-primary))]">{fmt(bidder.quoteAmount)}</p>
            <p className="text-2xs text-[hsl(var(--text-muted))]">{bidder.yearsExperience} yrs exp</p>
          </div>
          <div className="flex-shrink-0 flex items-center gap-2">
            <span className={`text-2xs font-semibold px-2 py-0.5 rounded-full ${bidderStatusColor(bidder.status)}`}>{bidder.status}</span>
            {bidder.status === 'Awarded' && bidder.vendorId ? (
              <Link
                href={`/vendor-workspace/${bidder.vendorId}`}
                className="btn-ghost py-1 px-2 text-xs flex items-center gap-1"
              >
                <ExternalLink size={12} /> Record
              </Link>
            ) : (
              <button
                onClick={() => toast.info(`Viewing quote for ${bidder.company}`)}
                className="btn-ghost py-1 px-2 text-xs"
              >
                <Eye size={12} />
              </button>
            )}
          </div>
        </div>
      ))}
      <button
        onClick={() => toast.info('Add bidder form coming soon.')}
        className="w-full py-2.5 border border-dashed border-[hsl(var(--border))] rounded-lg text-xs text-[hsl(var(--text-muted))] hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-colors flex items-center justify-center gap-1.5"
      >
        <Plus size={13} /> Add Bidder / Quote
      </button>
    </div>
  );
}

// ─── Intake Detail Modal ──────────────────────────────────────────────────────

function IntakeDetailModal({ item, onClose }: { item: IntakeItem; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[hsl(var(--surface))] rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[hsl(var(--border))] flex-shrink-0">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-2xs font-mono text-[hsl(var(--text-muted))]">{item.intakeRef}</span>
              <span className={`inline-flex items-center text-2xs font-semibold px-2 py-0.5 rounded-full ${intakeStatusColor(item.status)}`}>
                {item.status}
              </span>
              <span className={`inline-flex items-center text-2xs font-semibold px-2 py-0.5 rounded-full ${riskTierColor(item.riskTier)}`}>
                {item.riskTier} Risk
              </span>
              <span className={`inline-flex items-center text-2xs font-semibold px-2 py-0.5 rounded-full ${urgencyColor(item.urgency)}`}>
                {item.urgency.charAt(0).toUpperCase() + item.urgency.slice(1)}
              </span>
            </div>
            <h2 className="text-base font-bold text-[hsl(var(--text-primary))]">{item.projectTitle}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-[hsl(var(--background))] text-[hsl(var(--text-muted))] transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <XCircle size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {/* Section: Business Need */}
          <section>
            <h3 className="text-xs font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Briefcase size={12} /> Business Need
            </h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div>
                <p className="text-2xs text-[hsl(var(--text-muted))] mb-0.5">Project Title</p>
                <p className="text-xs font-medium text-[hsl(var(--text-primary))]">{item.projectTitle}</p>
              </div>
              <div>
                <p className="text-2xs text-[hsl(var(--text-muted))] mb-0.5">Submitted Date</p>
                <p className="text-xs font-medium text-[hsl(var(--text-primary))]">{item.submittedDate}</p>
              </div>
              <div>
                <p className="text-2xs text-[hsl(var(--text-muted))] mb-0.5">Requested By</p>
                <p className="text-xs font-medium text-[hsl(var(--text-primary))]">{item.requestedBy}</p>
              </div>
              <div>
                <p className="text-2xs text-[hsl(var(--text-muted))] mb-0.5">Department</p>
                <p className="text-xs font-medium text-[hsl(var(--text-primary))]">{item.department}</p>
              </div>
              <div>
                <p className="text-2xs text-[hsl(var(--text-muted))] mb-0.5">Urgency</p>
                <span className={`inline-flex items-center text-2xs font-semibold px-2 py-0.5 rounded-full ${urgencyColor(item.urgency)}`}>
                  {item.urgency.charAt(0).toUpperCase() + item.urgency.slice(1)}
                </span>
              </div>
              <div>
                <p className="text-2xs text-[hsl(var(--text-muted))] mb-0.5">Estimated Value</p>
                <p className="text-xs font-bold text-[hsl(var(--text-primary))]">{item.estimatedValue}</p>
              </div>
            </div>
          </section>

          <div className="border-t border-[hsl(var(--border))]" />

          {/* Section: Procurement */}
          <section>
            <h3 className="text-xs font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <DollarSign size={12} /> Procurement
            </h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div>
                <p className="text-2xs text-[hsl(var(--text-muted))] mb-0.5">Competitive Bid Required</p>
                <p className={`text-xs font-semibold ${item.competitiveBid === 'Yes' ? 'text-[hsl(var(--positive))]' : 'text-[hsl(var(--warning))]'}`}>
                  {item.competitiveBid === 'Yes' ? '✓ Yes — Competitive Bid' : '✗ No — Sole Source'}
                </p>
              </div>
              <div>
                <p className="text-2xs text-[hsl(var(--text-muted))] mb-0.5">Risk Tier</p>
                <span className={`inline-flex items-center text-2xs font-semibold px-2 py-0.5 rounded-full ${riskTierColor(item.riskTier)}`}>
                  {item.riskTier}
                </span>
              </div>
            </div>
          </section>

          <div className="border-t border-[hsl(var(--border))]" />

          {/* Section: Data */}
          <section>
            <h3 className="text-xs font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Database size={12} /> Data Types Involved
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {item.dataTypes.map((d) => (
                <span key={d} className="inline-flex items-center gap-1 text-2xs bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full">
                  <Database size={9} /> {d}
                </span>
              ))}
            </div>
          </section>

          <div className="border-t border-[hsl(var(--border))]" />

          {/* Section: Systems */}
          <section>
            <h3 className="text-xs font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Server size={12} /> Systems Accessed
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {item.systemsAccessed.map((s) => (
                <span key={s} className="inline-flex items-center gap-1 text-2xs bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-400 px-2 py-0.5 rounded-full">
                  <Server size={9} /> {s}
                </span>
              ))}
            </div>
          </section>

          {item.notes && (
            <>
              <div className="border-t border-[hsl(var(--border))]" />
              <section>
                <h3 className="text-xs font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <FileSearch size={12} /> Notes
                </h3>
                <p className="text-xs text-[hsl(var(--text-secondary))] leading-relaxed">{item.notes}</p>
              </section>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[hsl(var(--border))] flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="btn-secondary text-xs"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Intake Queue Panel ───────────────────────────────────────────────────────

function IntakeQueuePanel({ onCreateRFP }: { onCreateRFP: (item: IntakeItem) => void }) {
  const [filter, setFilter] = useState<IntakeItem['status'] | ''>('');
  const [viewingItem, setViewingItem] = useState<IntakeItem | null>(null);
  const { can } = useRole();

  const filtered = INTAKE_QUEUE.filter((i) => !filter || i.status === filter);
  const pendingCount = INTAKE_QUEUE.filter((i) => i.status === 'Pending RFP' || i.status === 'Needs Review').length;

  return (
    <div>
      {viewingItem && (
        <IntakeDetailModal item={viewingItem} onClose={() => setViewingItem(null)} />
      )}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Intake Submissions Queue</h3>
          <p className="text-xs text-[hsl(var(--text-muted))]">
            {INTAKE_QUEUE.length} submissions · {pendingCount} awaiting action
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as IntakeItem['status'] | '')}
            className="input-field text-xs py-1.5"
          >
            <option value="">All Statuses</option>
            <option value="Pending RFP">Pending RFP</option>
            <option value="RFP Created">RFP Created</option>
            <option value="Sole Source Approved">Sole Source Approved</option>
            <option value="Needs Review">Needs Review</option>
          </select>
          <Link
            href="/intake-form"
            className="btn-secondary text-xs flex items-center gap-1.5"
          >
            <Plus size={12} /> New Intake
          </Link>
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map((item) => (
          <div
            key={item.id}
            className="border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--surface))] overflow-hidden"
          >
            {/* Header row */}
            <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--background))]">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-2xs font-mono text-[hsl(var(--text-muted))]">{item.intakeRef}</span>
                  <span className={`inline-flex items-center text-2xs font-semibold px-2 py-0.5 rounded-full ${intakeStatusColor(item.status)}`}>
                    {item.status}
                  </span>
                  <span className={`inline-flex items-center text-2xs font-semibold px-2 py-0.5 rounded-full ${riskTierColor(item.riskTier)}`}>
                    {item.riskTier} Risk
                  </span>
                  <span className={`inline-flex items-center text-2xs font-semibold px-2 py-0.5 rounded-full ${urgencyColor(item.urgency)}`}>
                    {item.urgency.charAt(0).toUpperCase() + item.urgency.slice(1)}
                  </span>
                </div>
                <p className="text-sm font-semibold text-[hsl(var(--text-primary))] leading-snug">{item.projectTitle}</p>
                <p className="text-2xs text-[hsl(var(--text-muted))] mt-0.5">
                  {item.department} · Requested by {item.requestedBy} · Submitted {item.submittedDate}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-xs font-bold text-[hsl(var(--text-primary))]">{item.estimatedValue}</p>
                <p className="text-2xs text-[hsl(var(--text-muted))]">Est. Value</p>
              </div>
            </div>

            {/* Details row */}
            <div className="px-4 py-3 flex items-start gap-6 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="text-2xs font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wide mb-1">Data Types</p>
                <div className="flex flex-wrap gap-1">
                  {item.dataTypes.slice(0, 3).map((d) => (
                    <span key={d} className="inline-flex items-center gap-0.5 text-2xs bg-[hsl(var(--background))] border border-[hsl(var(--border))] px-1.5 py-0.5 rounded">
                      <Database size={9} className="text-[hsl(var(--text-muted))]" /> {d}
                    </span>
                  ))}
                  {item.dataTypes.length > 3 && (
                    <span className="text-2xs text-[hsl(var(--text-muted))]">+{item.dataTypes.length - 3} more</span>
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-2xs font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wide mb-1">Systems</p>
                <div className="flex flex-wrap gap-1">
                  {item.systemsAccessed.slice(0, 2).map((s) => (
                    <span key={s} className="inline-flex items-center gap-0.5 text-2xs bg-[hsl(var(--background))] border border-[hsl(var(--border))] px-1.5 py-0.5 rounded">
                      <Server size={9} className="text-[hsl(var(--text-muted))]" /> {s}
                    </span>
                  ))}
                  {item.systemsAccessed.length > 2 && (
                    <span className="text-2xs text-[hsl(var(--text-muted))]">+{item.systemsAccessed.length - 2} more</span>
                  )}
                </div>
              </div>
              <div className="flex-shrink-0">
                <p className="text-2xs font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wide mb-1">Competitive Bid</p>
                <span className={`text-2xs font-semibold ${item.competitiveBid === 'Yes' ? 'text-[hsl(var(--positive))]' : 'text-[hsl(var(--warning))]'}`}>
                  {item.competitiveBid === 'Yes' ? '✓ Required' : '✗ Sole Source'}
                </span>
              </div>
            </div>

            {/* Notes + Actions */}
            <div className="px-4 py-2.5 border-t border-[hsl(var(--border))] bg-[hsl(var(--background))] flex items-center justify-between gap-3">
              <p className="text-2xs text-[hsl(var(--text-muted))] flex-1 truncate">{item.notes}</p>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setViewingItem(item)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-[hsl(var(--border))] text-[hsl(var(--text-secondary))] hover:border-[hsl(var(--border-strong))] hover:text-[hsl(var(--text-primary))] transition-colors"
                >
                  <Eye size={12} /> View
                </button>
                {item.status === 'RFP Created' && (
                  <span className="inline-flex items-center gap-1 text-2xs text-[hsl(var(--positive))]">
                    <CheckSquare size={11} /> RFP Linked
                  </span>
                )}
                {(item.status === 'Pending RFP') && can('sourcing:createRFP') && (
                  <button
                    onClick={() => onCreateRFP(item)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[hsl(var(--accent))] text-white hover:opacity-90 transition-opacity"
                  >
                    <Plus size={12} /> Create RFP
                  </button>
                )}
                {item.status === 'Needs Review' && (
                  <button
                    onClick={() => toast.info(`Opening review for ${item.intakeRef}`)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors"
                  >
                    <AlertTriangle size={12} /> Review
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center h-32 text-center">
            <ClipboardList size={28} className="text-[hsl(var(--text-muted))] mb-2 opacity-40" />
            <p className="text-sm text-[hsl(var(--text-secondary))]">No intake submissions match this filter.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

type Tab = 'matrix' | 'scoring' | 'quotes' | 'intake';

export default function SourcingWorkspace() {
  const [selectedRFP, setSelectedRFP] = useState<RFP>(RFPS[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<RFPStatus | ''>('');
  const [activeTab, setActiveTab] = useState<Tab>('matrix');
  const { can } = useRole();

  const filteredRFPs = RFPS.filter((r) => {
    const q = searchQuery.toLowerCase();
    const matchQ = !q || r.title.toLowerCase().includes(q) || r.department.toLowerCase().includes(q);
    const matchStatus = !filterStatus || r.status === filterStatus;
    return matchQ && matchStatus;
  });

  const rfpBidders = BIDDERS.filter((b) => b.rfpId === selectedRFP.id);
  const activeBidders = rfpBidders.filter((b) => b.status !== 'Disqualified');

  const statusCounts = {
    total: RFPS.length,
    published: RFPS.filter((r) => r.status === 'Published').length,
    evaluation: RFPS.filter((r) => r.status === 'Evaluation').length,
    awarded: RFPS.filter((r) => r.status === 'Awarded').length,
  };

  const pendingIntake = INTAKE_QUEUE.filter((i) => i.status === 'Pending RFP' || i.status === 'Needs Review').length;

  const tabs: { key: Tab; label: string; icon: React.ElementType; badge?: number }[] = [
    { key: 'intake', label: 'Intake Queue', icon: ClipboardList, badge: pendingIntake > 0 ? pendingIntake : undefined },
    { key: 'matrix', label: 'Bidder Comparison', icon: BarChart2 },
    { key: 'scoring', label: 'Evaluation Scoring', icon: Star },
    { key: 'quotes', label: 'Quote Collection', icon: DollarSign },
  ];

  const handleCreateRFP = (item: IntakeItem) => {
    toast.success(`RFP created from ${item.intakeRef}`, {
      description: `"${item.projectTitle}" has been added to the RFP pipeline.`,
    });
    setActiveTab('matrix');
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Breadcrumb */}
      <div className="px-5 py-2 border-b border-[hsl(var(--border))] bg-[hsl(var(--background))] flex items-center gap-1.5 text-xs text-[hsl(var(--text-muted))] flex-shrink-0">
        <Link href="/executive-dashboard" className="flex items-center gap-1 hover:text-[hsl(var(--text-primary))] transition-colors">
          <Home size={12} /> Home
        </Link>
        <ChevronRightIcon size={12} />
        <span className="text-[hsl(var(--text-primary))] font-medium">Sourcing Workspace</span>
      </div>

      {/* Page header */}
      <div className="px-5 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex items-center justify-between gap-3 flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-[hsl(var(--text-primary))]">Sourcing Workspace</h1>
          <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">
            {statusCounts.published} published · {statusCounts.evaluation} in evaluation · {statusCounts.awarded} awarded
            {pendingIntake > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-amber-600 font-semibold">
                <Clock size={11} /> {pendingIntake} intake{pendingIntake !== 1 ? 's' : ''} pending
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            href="/intake-form"
            className="btn-secondary flex items-center gap-1.5"
          >
            <ClipboardList size={13} />
            New Intake
          </Link>
          <button
            onClick={() => toast.success('Export started', { description: 'Sourcing report being prepared.' })}
            className="btn-secondary"
          >
            <Download size={13} />
            Export Report
          </button>
          {can('sourcing:createRFP') && (
            <button
              onClick={() => toast.info('New RFP form coming soon.')}
              className="btn-primary"
            >
              <Plus size={13} />
              New RFP
            </button>
          )}
        </div>
      </div>

      {/* Summary strip */}
      <div className="flex border-b border-[hsl(var(--border))] bg-[hsl(var(--background))] flex-shrink-0 divide-x divide-[hsl(var(--border))]">
        {[
          { label: 'Total RFPs', value: statusCounts.total, icon: FileSearch, color: 'text-[hsl(var(--info))]' },
          { label: 'Published', value: statusCounts.published, icon: Eye, color: 'text-blue-600' },
          { label: 'In Evaluation', value: statusCounts.evaluation, icon: BarChart2, color: 'text-amber-600' },
          { label: 'Awarded', value: statusCounts.awarded, icon: Award, color: 'text-[hsl(var(--positive))]' },
          { label: 'Intake Pending', value: pendingIntake, icon: ClipboardList, color: pendingIntake > 0 ? 'text-amber-600' : 'text-[hsl(var(--text-muted))]' },
          { label: 'Active Bidders', value: BIDDERS.filter((b) => b.rfpId === selectedRFP.id && b.status !== 'Disqualified').length, icon: Users, color: 'text-violet-600' },
        ].map((s) => {
          const Ic = s.icon;
          return (
            <div key={s.label} className="flex items-center gap-2.5 px-4 py-2.5 flex-1">
              <Ic size={16} className={s.color} />
              <div>
                <p className="text-lg font-bold tabular-nums text-[hsl(var(--text-primary))] leading-none">{s.value}</p>
                <p className="text-2xs text-[hsl(var(--text-muted))] mt-0.5">{s.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Body: sidebar + main */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* RFP list sidebar */}
        <div className="w-72 flex-shrink-0 border-r border-[hsl(var(--border))] bg-[hsl(var(--background))] flex flex-col overflow-hidden">
          {/* Search + filter */}
          <div className="p-3 space-y-2 border-b border-[hsl(var(--border))]">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
              <input
                type="text"
                placeholder="Search RFPs…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-field pl-7 text-xs py-1.5 w-full"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as RFPStatus | '')}
              className="input-field text-xs py-1.5 w-full"
            >
              <option value="">All Statuses</option>
              {(['Draft', 'Published', 'Evaluation', 'Awarded', 'Cancelled', 'Closed'] as RFPStatus[]).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          {/* RFP list */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {filteredRFPs.map((rfp) => (
              <RFPStatusCard
                key={rfp.id}
                rfp={rfp}
                selected={selectedRFP.id === rfp.id}
                onClick={() => { setSelectedRFP(rfp); if (activeTab === 'intake') setActiveTab('matrix'); }}
              />
            ))}
            {filteredRFPs.length === 0 && (
              <p className="text-xs text-[hsl(var(--text-muted))] text-center py-8">No RFPs match your filters.</p>
            )}
          </div>
          {/* Intake shortcut */}
          <div className="p-3 border-t border-[hsl(var(--border))]">
            <button
              onClick={() => setActiveTab('intake')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                activeTab === 'intake' ?'bg-[hsl(var(--accent-light))] text-[hsl(var(--accent))] border border-[hsl(var(--accent))]' :'bg-[hsl(var(--surface))] border border-[hsl(var(--border))] text-[hsl(var(--text-secondary))] hover:border-[hsl(var(--border-strong))]'
              }`}
            >
              <span className="flex items-center gap-2">
                <ClipboardList size={13} />
                Intake Queue
              </span>
              {pendingIntake > 0 && (
                <span className="bg-amber-500 text-white text-2xs font-bold px-1.5 py-0.5 rounded-full">{pendingIntake}</span>
              )}
            </button>
          </div>
        </div>

        {/* Main panel */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* RFP detail header — hide when intake tab active */}
          {activeTab !== 'intake' && (
            <div className="px-5 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex-shrink-0">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center gap-1 text-2xs font-semibold px-2 py-0.5 rounded-full ${rfpStatusColor(selectedRFP.status)}`}>
                      {rfpStatusIcon(selectedRFP.status)}
                      {selectedRFP.status}
                    </span>
                    <span className="text-2xs text-[hsl(var(--text-muted))]">{selectedRFP.category}</span>
                    {selectedRFP.intakeRef && (
                      <span className="inline-flex items-center gap-1 text-2xs text-[hsl(var(--text-muted))] bg-[hsl(var(--background))] border border-[hsl(var(--border))] px-2 py-0.5 rounded-full">
                        <ClipboardList size={10} /> {selectedRFP.intakeRef}
                      </span>
                    )}
                  </div>
                  <h2 className="text-base font-bold text-[hsl(var(--text-primary))]">{selectedRFP.title}</h2>
                  <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">
                    {selectedRFP.department} · Owner: {selectedRFP.owner} · Close: {selectedRFP.closeDate}
                  </p>
                </div>
                <div className="flex items-start gap-3 flex-shrink-0">
                  {selectedRFP.status === 'Awarded' && selectedRFP.awardedVendorId && (
                    <Link
                      href={`/vendor-workspace/${selectedRFP.awardedVendorId}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-400 hover:bg-teal-200 dark:hover:bg-teal-900 transition-colors"
                    >
                      <ExternalLink size={12} />
                      {selectedRFP.awardedVendorName ?? 'Vendor Record'}
                    </Link>
                  )}
                  <div className="text-right">
                    <p className="text-xs text-[hsl(var(--text-muted))]">Estimated Value</p>
                    <p className="text-lg font-bold tabular-nums text-[hsl(var(--text-primary))]">{fmt(selectedRFP.estimatedValue)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex-shrink-0 px-5">
            {tabs.map((tab) => {
              const Ic = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors -mb-px ${
                    activeTab === tab.key
                      ? 'border-[hsl(var(--accent))] text-[hsl(var(--accent))]'
                      : 'border-transparent text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-secondary))]'
                  }`}
                >
                  <Ic size={13} />
                  {tab.label}
                  {tab.badge !== undefined && (
                    <span className="ml-1 bg-amber-500 text-white text-2xs font-bold px-1.5 py-0.5 rounded-full leading-none">
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto p-5">
            {/* Intake Queue tab */}
            {activeTab === 'intake' && (
              <IntakeQueuePanel onCreateRFP={handleCreateRFP} />
            )}

            {/* RFP-specific tabs */}
            {activeTab !== 'intake' && (
              <>
                {rfpBidders.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-48 text-center">
                    <Users size={32} className="text-[hsl(var(--text-muted))] mb-3 opacity-40" />
                    <p className="text-sm font-medium text-[hsl(var(--text-secondary))]">No bidders yet</p>
                    <p className="text-xs text-[hsl(var(--text-muted))] mt-1">Add bidders once this RFP is published.</p>
                    {selectedRFP.intakeRef && (
                      <p className="text-xs text-[hsl(var(--text-muted))] mt-1">
                        Intake ref: <span className="font-mono text-[hsl(var(--text-secondary))]">{selectedRFP.intakeRef}</span>
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                    {activeTab === 'matrix' && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Bidder Comparison Matrix</h3>
                            <p className="text-xs text-[hsl(var(--text-muted))]">
                              {rfpBidders.length} bidders · {activeBidders.length} active · Weighted scoring across 5 criteria
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 text-2xs text-[hsl(var(--text-muted))]">
                            <Star size={11} className="text-amber-500" /> Top scorer
                            <span className="ml-2 w-2 h-2 rounded-full bg-[hsl(var(--positive))] inline-block" /> ≥85
                            <span className="ml-1 w-2 h-2 rounded-full bg-[hsl(var(--warning))] inline-block" /> ≥70
                            <span className="ml-1 w-2 h-2 rounded-full bg-[hsl(var(--negative))] inline-block" /> &lt;70
                          </div>
                        </div>
                        <div className="border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--surface))] overflow-hidden">
                          <ComparisonMatrix bidders={rfpBidders} />
                        </div>
                      </div>
                    )}
                    {activeTab === 'scoring' && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Evaluation Scoring</h3>
                            <p className="text-xs text-[hsl(var(--text-muted))]">
                              Score each bidder across cost, technical fit, experience, security posture, and responsible bidder criteria
                            </p>
                          </div>
                          <div className="flex items-center gap-3 text-2xs text-[hsl(var(--text-muted))]">
                            {(Object.keys(EVAL_WEIGHTS) as (keyof EvalScore)[]).map((k) => {
                              const Ic = EVAL_ICONS[k];
                              return (
                                <span key={k} className="flex items-center gap-1">
                                  <Ic size={11} className={EVAL_COLORS[k]} />
                                  {Math.round(EVAL_WEIGHTS[k] * 100)}%
                                </span>
                              );
                            })}
                          </div>
                        </div>
                        <EvaluationScoringPanel bidders={rfpBidders} />
                      </div>
                    )}
                    {activeTab === 'quotes' && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Quote Collection</h3>
                            <p className="text-xs text-[hsl(var(--text-muted))]">
                              {rfpBidders.length} quotes received · Range: {fmt(Math.min(...rfpBidders.map((b) => b.quoteAmount)))} – {fmt(Math.max(...rfpBidders.map((b) => b.quoteAmount)))}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[hsl(var(--text-muted))]">
                            <TrendingUp size={13} />
                            Avg: {fmt(Math.round(rfpBidders.reduce((s, b) => s + b.quoteAmount, 0) / rfpBidders.length))}
                          </div>
                        </div>
                        <QuoteCollectionPanel bidders={rfpBidders} />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
